import { Component, Input } from '@angular/core';

/** Estatus de vuelo soportados por la pantalla atomizada de estatus. */
export type FlightStatusScreenStatus =
  | 'closed'
  | 'cancelled'
  | 'delayed'
  | 'early'
  | 'boarding'
  | 'unconfirmed';

/** Variante visual del badge de estatus. */
export type FlightStatusBadgeVariant = 'danger' | 'warning' | 'success' | 'boarding';

/** Icono del badge: triángulo de advertencia (inline) o cancelación (asset). */
export type FlightStatusBadgeIcon = 'warning' | 'cancel';

/** Tarjeta de ayuda de la sección "¿Qué puedo hacer ahora?". */
export interface FlightStatusHelpCard {
  /** Modificador del icono (mask coloreada con el token rosa). */
  iconClass: string;
  titleKey: string;
  subtitleKey: string;
}

/** Configuración visual y de comportamiento de la pantalla para cada estatus. */
export interface FlightStatusScreenConfig {
  status: FlightStatusScreenStatus;
  /** Variante de color del badge superior. */
  badgeVariant: FlightStatusBadgeVariant;
  /** Icono del badge superior. */
  badgeIcon: FlightStatusBadgeIcon;
  /** Llave i18n del texto del badge. */
  badgeLabelKey: string;
  /** El badge concatena el tiempo de afectación (+2h 5m / -15m). */
  badgeWithTime: boolean;
  /** Muestra la ruta "Ciudad origen → Ciudad destino" sobre el título. */
  showRoute: boolean;
  /** Llave i18n del título principal. */
  titleKey: string;
  /** Indica si el título requiere el parámetro cityName. */
  titleWithCity: boolean;
  /** Llave i18n del subtítulo bajo el título. */
  subtitleKey?: string;
  /** Llave i18n del encabezado de la sección de ayuda. */
  helpTitleKey?: string;
  /** Tarjetas de ayuda (variante cancelado). */
  helpCards: FlightStatusHelpCard[];
  /** Tarjeta informativa (variante cerrado). */
  infoCard?: {
    icon: string;
    titleKey: string;
    bodyKeyDomestic: string;
    bodyKeyInternational: string;
  };
  /** Muestra la columna de abordaje en los datos del vuelo. */
  showBoarding: boolean;
  /** El abordaje se muestra como "Ahora" (variante cerrado). */
  boardingNow: boolean;
  /** Los horarios del itinerario se muestran tachados (variante cancelado). */
  strikeTimes: boolean;
  /** El estatus termina el flujo del kiosko. */
  isTerminal: boolean;
  /** El estatus bloquea el avance del flujo. */
  shouldBlockFlow: boolean;
}

/**
 * Bloque de ayuda "¿Qué puedo hacer ahora?" compartido por las variantes
 * terminales que lo requieren (cancelado, demorado, adelantado y cambio sin
 * confirmar). Se centraliza aquí para no duplicar markup ni textos: mismas
 * llaves i18n, mismos iconos y el teléfono vive en i18n (callCenterSubtitle).
 */
const WHAT_CAN_I_DO_HELP_CARDS: FlightStatusHelpCard[] = [
  {
    iconClass: 'phone',
    titleKey: 'flightStatusScreen.callCenterTitle',
    subtitleKey: 'flightStatusScreen.callCenterSubtitle',
  },
  {
    iconClass: 'counter',
    titleKey: 'flightStatusScreen.counterTitle',
    subtitleKey: 'flightStatusScreen.counterSubtitle',
  },
];

/** Mapa de configuración por estatus. Todas las variantes son terminales y bloquean el flujo. */
export const FLIGHT_STATUS_SCREEN_CONFIG: Record<
  FlightStatusScreenStatus,
  FlightStatusScreenConfig
> = {
  closed: {
    status: 'closed',
    badgeVariant: 'boarding',
    badgeIcon: 'warning',
    badgeLabelKey: 'flightStatusScreen.badgeClosed',
    badgeWithTime: false,
    showRoute: false,
    titleKey: 'noticeOnBoarding.goToTheCounter',
    titleWithCity: false,
    subtitleKey: 'noticeOnBoarding.appreciate',
    helpCards: [],
    infoCard: {
      icon: 'assets/images/infoBlue.svg',
      titleKey: 'flightStatusScreen.docsClosedTitle',
      bodyKeyDomestic: 'flightStatusScreen.docsClosedBodyDomestic',
      bodyKeyInternational: 'flightStatusScreen.docsClosedBodyInternational',
    },
    showBoarding: true,
    boardingNow: true,
    strikeTimes: false,
    isTerminal: true,
    shouldBlockFlow: true,
  },
  boarding: {
    status: 'boarding',
    badgeVariant: 'boarding',
    badgeIcon: 'warning',
    badgeLabelKey: 'noticeOnBoarding.boarding',
    badgeWithTime: false,
    showRoute: false,
    titleKey: 'noticeOnBoarding.goToTheCounter',
    titleWithCity: false,
    subtitleKey: 'noticeOnBoarding.appreciate',
    helpCards: [],
    infoCard: {
      icon: 'assets/images/infoBlue.svg',
      titleKey: 'flightStatusScreen.docsClosedTitle',
      bodyKeyDomestic: 'flightStatusScreen.docsClosedBodyDomestic',
      bodyKeyInternational: 'flightStatusScreen.docsClosedBodyInternational',
    },
    showBoarding: true,
    boardingNow: true,
    strikeTimes: false,
    isTerminal: true,
    shouldBlockFlow: true,
  },
  unconfirmed: {
    status: 'unconfirmed',
    badgeVariant: 'warning',
    badgeIcon: 'warning',
    badgeLabelKey: 'flightStatusScreen.unconfirmedTitle',
    badgeWithTime: false,
    showRoute: false,
    titleKey: 'flightStatusScreen.unconfirmedTitle',
    titleWithCity: false,
    helpTitleKey: 'flightStatusScreen.whatCanIDo',
    helpCards: WHAT_CAN_I_DO_HELP_CARDS,
    showBoarding: false,
    boardingNow: false,
    strikeTimes: false,
    isTerminal: true,
    shouldBlockFlow: true,
  },
  cancelled: {
    status: 'cancelled',
    badgeVariant: 'danger',
    badgeIcon: 'cancel',
    badgeLabelKey: 'srIrops.cancelledFlight',
    badgeWithTime: false,
    showRoute: false,
    titleKey: 'flightStatusScreen.cancelledTitle',
    titleWithCity: true,
    helpTitleKey: 'flightStatusScreen.whatCanIDo',
    helpCards: WHAT_CAN_I_DO_HELP_CARDS,
    showBoarding: false,
    boardingNow: false,
    strikeTimes: true,
    isTerminal: true,
    shouldBlockFlow: true,
  },
  delayed: {
    status: 'delayed',
    badgeVariant: 'warning',
    badgeIcon: 'warning',
    badgeLabelKey: 'flightStatusScreen.badgeDelay',
    badgeWithTime: true,
    showRoute: true,
    titleKey: 'flightStatusScreen.checkinAtCounterTitle',
    titleWithCity: false,
    subtitleKey: 'flightStatusScreen.delayedSubtitle',
    helpTitleKey: 'flightStatusScreen.whatCanIDo',
    helpCards: WHAT_CAN_I_DO_HELP_CARDS,
    showBoarding: true,
    boardingNow: false,
    strikeTimes: false,
    isTerminal: true,
    shouldBlockFlow: true,
  },
  early: {
    status: 'early',
    badgeVariant: 'success',
    badgeIcon: 'warning',
    badgeLabelKey: 'flightStatusScreen.badgeEarly',
    badgeWithTime: true,
    showRoute: true,
    titleKey: 'flightStatusScreen.checkinAtCounterTitle',
    titleWithCity: false,
    subtitleKey: 'flightStatusScreen.earlySubtitle',
    helpTitleKey: 'flightStatusScreen.whatCanIDo',
    helpCards: WHAT_CAN_I_DO_HELP_CARDS,
    showBoarding: true,
    boardingNow: false,
    strikeTimes: false,
    isTerminal: true,
    shouldBlockFlow: true,
  },
};

/**
 * Pantalla atomizada de estatus de vuelo (CI-21670).
 *
 * Renderiza la vista terminal de estatus (cerrado, cancelado, demorado o
 * adelantado) con badge de estatus, título, itinerario del vuelo y las
 * secciones de ayuda/información que indica el diseño por variante.
 * El componente es presentacional: el padre resuelve el estatus y provee
 * los datos del vuelo; navbar y footer permanecen en la página.
 */
@Component({
  selector: 'am-flight-status-screen',
  templateUrl: './am-flight-status-screen.component.html',
  styleUrls: ['./am-flight-status-screen.component.scss'],
  standalone: false,
})
export class AmFlightStatusScreenComponent {
  /**
   * Estatus a renderizar; determina badge, título y secciones.
   * @default 'closed'
   */
  @Input() status: FlightStatusScreenStatus = 'closed';

  /** Nombre de la ciudad destino (parámetro del título). */
  @Input() titleDest = '';

  /** Código IATA del aeropuerto destino (imagen ilustrativa). */
  @Input() airport = '';

  /** Número de vuelo. */
  @Input() flight = '';

  /** Código IATA de salida (itinerario). */
  @Input() departureAirport = '';

  /** Código IATA de llegada (itinerario). */
  @Input() arrivalAirport = '';

  /** Ciudad de salida (ruta sobre el título). */
  @Input() departureCity = '';

  /** Ciudad de llegada (ruta sobre el título). */
  @Input() arrivalCity = '';

  /** Hora de salida efectiva (nueva si hay afectación). */
  @Input() departureTime = '';

  /** Hora de llegada efectiva (nueva si hay afectación). */
  @Input() arrivalTime = '';

  /** Hora de salida original; solo cuando difiere de la efectiva. */
  @Input() originalDepartureTime: string | null = null;

  /** Hora de llegada original; solo cuando difiere de la efectiva. */
  @Input() originalArrivalTime: string | null = null;

  /** Hora de abordaje efectiva (HH:mm). */
  @Input() boardingTime = '';

  /** Hora de abordaje original (HH:mm); solo cuando difiere. */
  @Input() originalBoardingTime: string | null = null;

  /** Duración total del vuelo, p. ej. "6h 5m". */
  @Input() durationLabel = '';

  /** Aeropuertos de conexión (IATA separados por coma). */
  @Input() connections = '';

  /** Vuelo doméstico; selecciona el cuerpo de la tarjeta informativa. */
  @Input() isDomestic = false;

  /** Diferencia de tiempo para demorado/adelantado (badge). */
  @Input() timeDif: { hours: number; minutes: number; status?: string } | null = null;

  /** Configuración efectiva según el estatus; cae a closed ante valores desconocidos. */
  get config(): FlightStatusScreenConfig {
    return FLIGHT_STATUS_SCREEN_CONFIG[this.status] || FLIGHT_STATUS_SCREEN_CONFIG.closed;
  }

  /** Clases de la variante de color del badge. */
  get badgeClass(): Record<string, boolean> {
    return {
      'AmFlightStatusScreen_badge--danger': this.config.badgeVariant === 'danger',
      'AmFlightStatusScreen_badge--warning': this.config.badgeVariant === 'warning',
      'AmFlightStatusScreen_badge--success': this.config.badgeVariant === 'success',
      'AmFlightStatusScreen_badge--boarding': this.config.badgeVariant === 'boarding',
    };
  }

  /** Tiempo de afectación del badge, p. ej. "+2h 5m" (demora) o "-15m" (adelanto). */
  get badgeTime(): string {
    if (!this.config.badgeWithTime || !this.timeDif) {
      return '';
    }
    const { hours, minutes } = this.timeDif;
    if (!hours && !minutes) {
      return '';
    }
    const sign = this.status === 'early' ? '-' : '+';
    const hoursPart = hours ? `${hours}h ` : '';
    return `${sign}${hoursPart}${minutes}m`;
  }

  /** Los horarios efectivos difieren de los originales (demorado/adelantado). */
  get hasTimeChange(): boolean {
    return this.status === 'delayed' || this.status === 'early';
  }

  /** Clase de color para los horarios nuevos según la variante. */
  get timeChangeClass(): Record<string, boolean> {
    return {
      'AmFlightStatusScreen_value--delayed': this.status === 'delayed',
      'AmFlightStatusScreen_value--early': this.status === 'early',
    };
  }

  /** Llave i18n del cuerpo de la tarjeta informativa según el tipo de vuelo. */
  get infoCardBodyKey(): string {
    if (!this.config.infoCard) {
      return '';
    }
    return this.isDomestic
      ? this.config.infoCard.bodyKeyDomestic
      : this.config.infoCard.bodyKeyInternational;
  }
}
